import { useCallback, useEffect, useRef, useState } from "react";

/**
 * usePSTrackReorder
 *
 * Encapsulates the "drag the color-bar grip to reorder a track" gesture
 * for the Production Suite tracks column. Uses pointer events with a
 * live-updating DOM indicator (no React re-renders during the drag) to
 * stay 60fps, mirroring the pattern used by the clip drag physics.
 *
 * Usage:
 *   const {
 *     reorderingId,       // string | null — id currently being dragged
 *     onGripPointerDown,  // attach to the grip handle on each track row
 *     dropLineRef,        // <div ref> for the horizontal drop indicator
 *     trackLabelsRef,     // <div ref> for the tracks column container
 *   } = usePSTrackReorder({ tracks, setTracks });
 *
 * The drop line is positioned by writing style.top directly; the caller
 * must render `<div ref={dropLineRef}>` somewhere inside the same
 * relatively-positioned container as the track rows.
 */
export default function usePSTrackReorder({ tracks, setTracks }) {
  const [reorderingId, setReorderingId] = useState(null);
  const trackLabelsRef = useRef(null);
  const dropLineRef = useRef(null);

  // Keep the latest tracks around without re-binding pointer handlers.
  const tracksRef = useRef(tracks);
  useEffect(() => { tracksRef.current = tracks; }, [tracks]);

  // Live state for the drag — mutated directly, avoids re-renders.
  const dragRef = useRef({
    active: false,
    trackId: null,
    // The insertion index (in the CURRENT tracks array) — 0..tracks.length.
    // e.g. targetIdx=2 means "drop between existing indexes 1 and 2".
    targetIdx: 0,
  });

  const onGripPointerDown = useCallback((e, trackId) => {
    // Left-button only; ignore right-click (that's the context menu)
    // and any mouse button we don't recognise.
    if (e.button !== 0) return;
    e.preventDefault();
    e.stopPropagation();
    const container = trackLabelsRef.current;
    if (!container) return;

    dragRef.current.active = true;
    dragRef.current.trackId = trackId;
    dragRef.current.targetIdx = tracksRef.current.findIndex((t) => t.id === trackId);
    setReorderingId(trackId);

    const onMove = (ev) => {
      if (!dragRef.current.active) return;
      const rows = Array.from(
        container.querySelectorAll("[data-ps-track-row]"),
      );
      if (rows.length === 0) return;
      const y = ev.clientY;

      // Compute which "gap" the cursor is closest to. Gap i sits above
      // row i. Gap rows.length sits below the last row.
      let targetIdx = rows.length;
      for (let i = 0; i < rows.length; i++) {
        const r = rows[i].getBoundingClientRect();
        if (y < r.top + r.height / 2) {
          targetIdx = i;
          break;
        }
      }

      dragRef.current.targetIdx = targetIdx;

      // Draw the drop indicator line. Positioned relative to the
      // trackLabels container (which we require to be position:relative).
      const line = dropLineRef.current;
      if (line) {
        const cRect = container.getBoundingClientRect();
        let top;
        if (targetIdx >= rows.length) {
          const last = rows[rows.length - 1].getBoundingClientRect();
          top = last.bottom - cRect.top + container.scrollTop;
        } else {
          const r = rows[targetIdx].getBoundingClientRect();
          top = r.top - cRect.top + container.scrollTop;
        }
        line.style.top = `${top}px`;
        line.style.opacity = "1";
      }
    };

    const onUp = () => {
      if (!dragRef.current.active) return;
      dragRef.current.active = false;

      const line = dropLineRef.current;
      if (line) line.style.opacity = "0";

      const { trackId: draggedId, targetIdx } = dragRef.current;
      dragRef.current.trackId = null;
      setReorderingId(null);

      setTracks((prev) => {
        const fromIdx = prev.findIndex((t) => t.id === draggedId);
        if (fromIdx === -1) return prev;
        // Convert an "insertion gap" index into a valid destination
        // that accounts for the fact we're removing the source first.
        let toIdx = targetIdx;
        if (toIdx > fromIdx) toIdx -= 1;
        if (toIdx === fromIdx) return prev; // nothing to do
        const next = [...prev];
        const [moved] = next.splice(fromIdx, 1);
        next.splice(toIdx, 0, moved);
        return next;
      });

      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      window.removeEventListener("pointercancel", onUp);
    };

    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    window.addEventListener("pointercancel", onUp);
  }, [setTracks]);

  return { reorderingId, onGripPointerDown, dropLineRef, trackLabelsRef };
}
